import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BarChart3, Download, RefreshCcw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { Sparkline, TrendIndicator } from "@/components/reports/Sparkline";

const TITLES = {
  affiliate: "Affiliate Report",
  delivery:  "Delivery Report",
  partner:   "Partner Report",
  franchise: "Franchise Report",
  store:     "Store Report",
  training:  "Training Report",
  admin:     "Cross-Module Rollup",
};

const RANGE_OPTIONS = [
  { value: "7d",  label: "Last 7 days"  },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time"     },
];

const DEFAULT_RANGE = "30d";

// Primitive metric key → trend key (sparkline). When the metric has no
// corresponding trend the cell renders without a sparkline.
const TREND_ALIAS = {
  // Affiliate
  clicks: "clicks", total_clicks: "clicks",
  conversions: "conversions", total_conversions: "conversions",
  pending_commission_cents: "commissions_cents",
  paid_commission_cents:    "commissions_cents",
  // Delivery
  orders_total: "orders",
  partner_orders_total: "orders",
  delivered: "delivered",
  orders_delivered: "delivered",
  driver_earnings_total_cents: "earnings_cents",
  earnings_total_cents: "earnings_cents",
  // Partner
  partner_orders_completed: "completed",
  completed: "completed",
  revenue_total_cents: "revenue_cents",
  partner_revenue_total_cents: "revenue_cents",
  // Franchise
  orders_completed: "completed",
  // Store
  delivered_orders:        "order_volume",
  partner_completed_orders: "partner_completed",
  // Training
  certifications_issued:  "certifications",
  active_certifications:  "certifications",
  test_attempts:          "attempts",
  active_learners:        "module_completions",
  // Admin rollup (top-level trends)
  ecosystem_growth: "ecosystem_growth",
};

function lookupTrend(metricKey, trends) {
  if (!trends) return null;
  if (trends[metricKey]) return trends[metricKey];
  const alias = TREND_ALIAS[metricKey];
  return alias ? trends[alias] || null : null;
}

function MetricRow({ moduleKey, metricKey, value, trends }) {
  const trend = lookupTrend(metricKey, trends);
  return (
    <tr data-testid={`report-${moduleKey}-metric-${metricKey}`} className="border-b border-white/5">
      <td className="px-4 py-2 text-lifted-text">{metricKey}</td>
      <td className="font-anton text-lifted-green text-lg whitespace-nowrap">
        {Array.isArray(value) ? value.length + " items" : String(value ?? "—")}
      </td>
      <td className="px-2 py-2">
        {trend && trend.series && trend.series.length > 1 ? (
          <Sparkline data={trend.series} direction={trend.direction}
                     testId={`report-${moduleKey}-spark-${metricKey}`} />
        ) : null}
      </td>
      <td className="pr-4 py-2">
        {trend ? (
          <TrendIndicator delta={trend.delta_pct} direction={trend.direction}
                          testId={`report-${moduleKey}-trend-${metricKey}`} />
        ) : null}
      </td>
    </tr>
  );
}

function RangePills({ value, onChange }) {
  return (
    <div className="inline-flex glass-panel p-1 gap-1" data-testid="reports-range-pills">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={o.value}
          data-testid={`reports-range-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-md font-bebas tracking-[0.18em] text-[0.72rem] transition ${
            value === o.value
              ? "bg-lifted-green/15 text-lifted-green"
              : "text-lifted-text hover:bg-white/5"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function MyReports() {
  const [params, setParams] = useSearchParams();
  const range = RANGE_OPTIONS.find((o) => o.value === params.get("range"))?.value || DEFAULT_RANGE;
  const setRange = (r) => setParams({ range: r }, { replace: true });

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/reports/me"); setData(data); }
      catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, []);

  if (err) return <div className="text-rose-300" data-testid="my-reports-error">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;

  return (
    <div data-testid="my-reports" className="space-y-8">
      <header className="fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-2"><BarChart3 className="h-3 w-3" /> Phase 11 · Reporting</div>
          <h1 className="headline-xl">Reports for you</h1>
          <p className="text-lifted-dim mt-2 text-sm">
            Role-aware visibility — you see what's relevant to your roles. No more, no less.
          </p>
        </div>
        <RangePills value={range} onChange={setRange} />
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.reports.map((r) => (
          <Link
            key={r.key}
            to={`/reports/${r.key}?range=${range}`}
            data-testid={`reports-tile-${r.key}`}
            className="glass-panel p-5 hover:-translate-y-1 transition-transform"
          >
            <div className="eyebrow !text-lifted-text-dim">{r.scope === "self" ? "Your slice" : "Ecosystem"}</div>
            <div className="font-anton uppercase text-xl mt-1">{TITLES[r.key] || r.title}</div>
            <div className="text-xs text-lifted-dim mt-2">scope: {r.scope} · window: {range}</div>
          </Link>
        ))}
        {data.reports.length === 0 && (
          <div className="text-lifted-mute md:col-span-3 text-center py-8">
            No reports available for your current roles.
          </div>
        )}
      </section>
    </div>
  );
}

export function ReportModule({ moduleKey }) {
  const [params, setParams] = useSearchParams();
  const range = RANGE_OPTIONS.find((o) => o.value === params.get("range"))?.value || DEFAULT_RANGE;
  const setRange = (r) => setParams({ range: r }, { replace: true });

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const endpoint = moduleKey === "admin" ? "/reports/admin/rollup" : `/reports/${moduleKey}`;
  const selfEndpoint = ["affiliate", "delivery", "partner", "franchise"].includes(moduleKey)
    ? `/reports/${moduleKey}/me` : null;

  const load = async () => {
    setBusy(true); setErr("");
    try {
      // Try admin scope first; if 403, fall back to /me (still windowed).
      try {
        const { data } = await api.get(endpoint, { params: { range } });
        setData(data);
      } catch (e) {
        if (e.response?.status === 403 && selfEndpoint) {
          const { data } = await api.get(selfEndpoint, { params: { range } });
          setData(data);
        } else throw e;
      }
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [moduleKey, range]); // eslint-disable-line react-hooks/exhaustive-deps

  // Relative /api path so the httpOnly cookie travels same-origin for the download.
  const csvUrl = `/api${endpoint}?range=${range}&format=csv`;

  if (err) return <div className="text-rose-300" data-testid={`report-${moduleKey}-error`}>{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>;

  // Top-level partitioning:
  //   primitives → scalar / array values (rendered in the main metric table)
  //   nested    → nested module dicts (each gets its own panel)
  //   trends    → sparkline payloads (rendered inline + as a top-level grid for admin rollup)
  const trends = (data.trends && typeof data.trends === "object" && !Array.isArray(data.trends))
    ? data.trends : null;
  const skip = new Set(["trends"]);
  const primitives = Object.entries(data).filter(([k, v]) =>
    !skip.has(k) && (!(v && typeof v === "object") || Array.isArray(v))
  );
  const nested = Object.entries(data).filter(([k, v]) =>
    !skip.has(k) && v && typeof v === "object" && !Array.isArray(v)
  );

  return (
    <div data-testid={`report-${moduleKey}`} className="space-y-8">
      <header className="fade-up flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-2"><BarChart3 className="h-3 w-3" /> Report</div>
          <h1 className="headline-xl">{TITLES[moduleKey] || moduleKey}</h1>
          <div className="text-xs text-lifted-dim mt-1" data-testid={`report-${moduleKey}-range-label`}>
            Window: <span className="text-lifted-green">{RANGE_OPTIONS.find((o) => o.value === range)?.label}</span>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <RangePills value={range} onChange={setRange} />
          <button onClick={load} disabled={busy} className="btn-ghost" data-testid={`report-${moduleKey}-refresh`}>
            <RefreshCcw className="h-3 w-3" /> Refresh
          </button>
          <a href={csvUrl} className="btn-lifted" data-testid={`report-${moduleKey}-csv`}>
            <Download className="h-3 w-3" /> CSV
          </a>
        </div>
      </header>

      {moduleKey === "admin" && trends ? (
        <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="report-admin-ecosystem-trends">
          {Object.entries(trends).map(([k, t]) => (
            <div key={k} className="glass-panel p-4" data-testid={`report-admin-trend-${k}`}>
              <div className="eyebrow !text-lifted-text-dim text-[0.65rem]">{k}</div>
              <div className="font-anton text-2xl text-lifted-green mt-1">{t.total ?? "—"}</div>
              <div className="flex items-center justify-between mt-2">
                <Sparkline data={t.series} direction={t.direction} width={120} height={28} />
                <TrendIndicator delta={t.delta_pct} direction={t.direction} />
              </div>
            </div>
          ))}
        </section>
      ) : null}

      <section className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">Metric</th>
              <th className="text-right pr-4">Value</th>
              <th className="px-2">Trend</th>
              <th className="pr-4">Δ</th>
            </tr>
          </thead>
          <tbody>
            {primitives.map(([k, v]) => (
              <MetricRow key={k} moduleKey={moduleKey} metricKey={k} value={v} trends={trends} />
            ))}
            {primitives.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-lifted-mute">No metrics in this view.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {nested.map(([k, v]) => {
        const subTrends = (v.trends && typeof v.trends === "object" && !Array.isArray(v.trends)) ? v.trends : null;
        const entries = Object.entries(v).filter(([kk]) => kk !== "trends");
        return (
          <section key={k} className="glass-panel overflow-hidden" data-testid={`report-${moduleKey}-section-${k}`}>
            <div className="eyebrow !text-lifted-text-dim px-4 pt-4 pb-3">{k}</div>
            <table className="w-full text-sm">
              <tbody>
                {entries.map(([kk, vv]) => {
                  // Render nested scalar metrics with sparklines when applicable.
                  if (vv && typeof vv === "object" && !Array.isArray(vv)) {
                    return (
                      <tr key={kk} className="border-b border-white/5">
                        <td className="px-4 py-2 text-lifted-text">{kk}</td>
                        <td className="font-anton text-lifted-green text-sm" colSpan={3}>
                          {JSON.stringify(vv)}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <MetricRow
                      key={kk}
                      moduleKey={`${moduleKey}-${k}`}
                      metricKey={kk}
                      value={vv}
                      trends={subTrends}
                    />
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
